'use client';

import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import StepIndicator from './StepIndicator';
import Step1VehicleDetails from './Step1VehicleDetails';
import Step2FaultDescription from './Step2FaultDescription';
import Step3PartsUsed from './Step3PartsUsed';
import Step4SignatureSubmit from './Step4SignatureSubmit';
import JobCardSuccess from './JobCardSuccess';
import { generateJobCardPDF } from '@/lib/generateJobCardPDF';
import { addJobCard } from '@/lib/jobCardStore';

export interface JobCardFormData {
  // Step 1
  jobNumber: string;
  date: string;
  technician: string;
  branch: string;
  vehicleReg: string;
  vin: string;
  make: string;
  model: string;
  year: string;
  mileage: string;
  // Step 2
  faultReported: string;
  faultsFound: string;
  repairsDone: string;
  faultPhotos: FileList | null;
  completionPhotos: FileList | null;
  // Step 3
  parts: { partNumber: string; partName: string; qty: number; stockAvailable: number }[];
  // Step 4
  branchRepName: string;
  signatureData: string;
}

const STEPS = [
  { id: 1, label: 'Vehicle Details' },
  { id: 2, label: 'Fault Description' },
  { id: 3, label: 'Parts Used' },
  { id: 4, label: 'Sign & Submit' },
];

export default function JobCardWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedJobNo, setSubmittedJobNo] = useState('');
  const [submittedData, setSubmittedData] = useState<JobCardFormData | null>(null);
  const [driveLink, setDriveLink] = useState<string | null>(null);
  const [drivePath, setDrivePath] = useState<string | null>(null);
  const [driveError, setDriveError] = useState<string | null>(null);

  const methods = useForm<JobCardFormData>({
    defaultValues: {
      jobNumber: `#${Date.now().toString().slice(-4)}`,
      date: new Date().toLocaleDateString('en-ZA'),
      technician: '',
      branch: '',
      vehicleReg: '',
      vin: '',
      make: '',
      model: '',
      year: '',
      mileage: '',
      faultReported: '',
      faultsFound: '',
      repairsDone: '',
      faultPhotos: null,
      completionPhotos: null,
      parts: [],
      branchRepName: '',
      signatureData: '',
    },
    mode: 'onChange',
  });

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async (data: JobCardFormData) => {
    setSubmitting(true);
    setDriveLink(null);
    setDrivePath(null);
    setDriveError(null);

    try {
      // Persist job card to Supabase
      await addJobCard({
        jobNumber: data.jobNumber,
        date: data.date,
        technician: data.technician,
        branch: data.branch,
        vehicleReg: data.vehicleReg,
        vin: data.vin || '',
        make: data.make,
        model: data.model,
        year: data.year,
        mileage: data.mileage || '',
        faultReported: data.faultReported || '',
        faultsFound: data.faultsFound || '',
        repairsDone: data.repairsDone || '',
        branchRepName: data.branchRepName,
        signatureData: data.signatureData,
        parts: data.parts,
        status: data.signatureData && data.signatureData.trim() !== '' ? 'signed' : 'active',
      });
    } catch (err) {
      console.error('Failed to save job card to Supabase:', err);
    }

    // Generate PDF and upload to Drive
    let pdfBase64: string | null = null;
    try {
      pdfBase64 = await generateJobCardPDF(data);
    } catch (err) {
      console.error('PDF generation failed:', err);
    }

    if (pdfBase64) {
      try {
        const driveRes = await fetch('/api/drive-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pdfBase64,
            branch: data.branch,
            vehicleReg: data.vehicleReg,
            jobNumber: data.jobNumber,
          }),
        });
        const driveData = await driveRes.json();
        if (driveRes.ok && driveData.success) {
          setDriveLink(driveData.fileLink || null);
          setDrivePath(driveData.path || null);
        } else {
          setDriveError(driveData.error || 'Drive upload failed');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Drive upload failed';
        setDriveError(msg);
        console.error('Drive upload error:', err);
      }
    }

    setSubmittedJobNo(data.jobNumber);
    setSubmittedData(data);
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <JobCardSuccess
        jobNo={submittedJobNo}
        formData={submittedData}
        driveLink={driveLink}
        drivePath={drivePath}
        driveError={driveError}
        onNew={() => {
          setSubmitted(false);
          setCurrentStep(1);
          methods.reset({
            jobNumber: `#${Date.now().toString().slice(-4)}`,
            date: new Date().toLocaleDateString('en-ZA'),
          });
          setSubmittedData(null);
          setDriveLink(null);
          setDrivePath(null);
          setDriveError(null);
        }}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)} noValidate>
        {/* Tablet-optimised layout: step indicator collapses on tablet portrait */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 lg:gap-6">
          {/* Step indicator — hidden on small tablet portrait, shown on xl */}
          <div className="hidden xl:block xl:col-span-1">
            <StepIndicator steps={STEPS} currentStep={currentStep} />
          </div>

          {/* Mobile/tablet step indicator (horizontal) */}
          <div className="xl:hidden">
            <div className="card-surface p-3 flex items-center justify-between overflow-x-auto scrollbar-thin">
              {STEPS.map((step, idx) => (
                <div key={step.id} className="flex items-center gap-1.5 flex-shrink-0">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      currentStep === step.id
                        ? 'bg-primary text-white'
                        : currentStep > step.id
                        ? 'bg-positive/20 text-positive' :'bg-secondary text-muted-foreground'
                    }`}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span
                    className={`text-xs font-medium whitespace-nowrap ${
                      currentStep === step.id ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <div className="w-6 h-px bg-border mx-1 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form content */}
          <div className="xl:col-span-3">
            {/* Touch-friendly card with larger padding on tablet */}
            <div className="card-surface p-4 sm:p-6 lg:p-8">
              {currentStep === 1 && <Step1VehicleDetails />}
              {currentStep === 2 && <Step2FaultDescription />}
              {currentStep === 3 && <Step3PartsUsed />}
              {currentStep === 4 && (
                <Step4SignatureSubmit submitting={submitting} />
              )}

              {/* Sticky footer nav — touch-friendly height */}
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-border">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="btn-secondary disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px] px-5"
                >
                  ← Back
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    Step {currentStep} of {STEPS.length}
                  </span>
                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn-primary min-h-[44px] px-5"
                    >
                      Next Step →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary min-w-[160px] min-h-[44px] justify-center"
                    >
                      {submitting ? (
                        <>
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Saving…
                        </>
                      ) : (
                        '✓ Submit Job Card'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}